import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { flowNodeColor } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import type {
  CanvasExtraData,
  ChatExtraData,
  EmailExtraData,
  MessengerExtraData,
  PhoneExtraData,
} from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';

export type GetDisplayName<TExtraData> = (payload: TExtraData) => string;

export const createInitNode = <TExtraData,>(
  getDisplayName: GetDisplayName<TExtraData>,
) =>
  function InitNode(props: GenericNodeProps<unknown, TExtraData>): JSX.Element {
    const { context } = props;
    const { extraData } = context;

    const name = getDisplayName(extraData);

    return (
      <TextContent>
        {useMemo(
          () => (
            <TextEllipsis>
              <Text weight="normal" text="To" />
              <Text weight="semibold" text={name} />
            </TextEllipsis>
          ),
          [name],
        )}
      </TextContent>
    );
  };

export const createInitNodeAttributes = <TExtraData extends CanvasExtraData>(
  attributes: {
    iconName: GenericNodeAttributes['iconName'];
    label: GenericNodeAttributes['label'];
  },
  getDisplayName: GetDisplayName<TExtraData>,
): GenericNodeAttributes<unknown, TExtraData> => ({
  color: hexStringToNumber(flowNodeColor.default),
  translatePayload: ({ children: [child] }) => [
    { id: uuid(), connection: child || uuid(), label: 'arrow' },
  ],
  updateConnection: (node) => node,
  NodeContent: createInitNode<TExtraData>(getDisplayName),
  ...attributes,
});

export const InitNodeChatAttributes = createInitNodeAttributes<ChatExtraData>(
  {
    iconName: 'chat',
    label: 'Incoming chat',
  },
  (data: ChatExtraData) => data.name || 'Name your flow',
);

export const InitNodeEmailAttributes = createInitNodeAttributes<EmailExtraData>(
  {
    iconName: 'chat',
    label: 'Incoming email',
  },
  (data: EmailExtraData) => data.name || 'Name your flow',
);

export const InitNodePhoneAttributes = createInitNodeAttributes<PhoneExtraData>(
  {
    iconName: 'phone',
    label: 'Incoming call',
  },
  (data: PhoneExtraData) => {
    const { number } = data;
    const isNumberSet = number !== 'local';

    return isNumberSet
      ? numberFormatterInternational({ phoneNumber: number })
      : 'No number selected';
  },
);

export const InitNodeMessengerAttributes =
  createInitNodeAttributes<MessengerExtraData>(
    {
      iconName: 'messenger',
      label: 'Incoming chat',
    },
    (data: MessengerExtraData) => data.name || 'Untitled',
  );

export const SubflowInitNodeMessengerAttributes =
  createInitNodeAttributes<MessengerExtraData>(
    {
      iconName: 'arrow-down',
      label: 'Subflow start',
    },
    (data: MessengerExtraData) => data.name || 'Untitled',
  );
